import { useMutation } from "@apollo/client/react";
import {
    CREATE_AMENDMENT,
    DELETE_AMENDMENT,
    GET_MISSION_AMENDMENTS,
    UPDATE_AMENDMENT,
} from "../graphql/amendment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CreateAmendmentInput } from "../__generated__/graphql";
import { MissionAmendmentFieldsFragment } from "../__generated__/operations/graphql";
import { MarkRequired } from "ts-essentials";
import { useDebouncedCallback } from "use-debounce";
import { usePrevious } from "./use-previous";
import { isEqual } from "lodash";
import { useCatchLazyQueryError } from "./errors";
import graphqlConfig from "../configs/graphql.config";

export enum AmendmentType {
    Salary,
    ExtendContract,
}

type UseAmendmentParams = {
    missionId?: string;
    missionDraftAmendment?: MissionAmendmentFieldsFragment;
    values: MarkRequired<Partial<Omit<CreateAmendmentInput, "missionId">>, "applyDate">; // List of values that needs to be saved
};

export const useCreateUpdateDeleteDraftAmendment = ({
    missionId,
    missionDraftAmendment,
    values,
}: UseAmendmentParams) => {
    const [createAmendment] = useMutation(CREATE_AMENDMENT);
    const [updateAmendment] = useMutation(UPDATE_AMENDMENT);
    const [deleteAmendment] = useMutation(DELETE_AMENDMENT);

    const [isLoading, setIsLoading] = useState(false);
    const isCreatingRef = useRef(false);

    const previousValues = usePrevious(values);

    const amendmentHasChanges = useMemo(
        () =>
            Object.keys(values).some(
                (key) => key !== "applyDate" && !!values[key as keyof typeof values],
            ),
        [values],
    );

    const updateDraftAmendment = useCallback(async () => {
        if (missionId && amendmentHasChanges && missionDraftAmendment) {
            await updateAmendment({
                variables: { amendmentId: missionDraftAmendment.id, input: { ...values } },
            });
        }
        setIsLoading(false);
    }, [amendmentHasChanges, missionDraftAmendment, missionId, updateAmendment, values]);

    const debouncedUpdateDraftAmendment = useDebouncedCallback(updateDraftAmendment, 1000);

    const createOrDeleteDraftAmendment = useCallback(async () => {
        if (missionId && amendmentHasChanges && !missionDraftAmendment && !isCreatingRef.current) {
            isCreatingRef.current = true;
            setIsLoading(true);
            try {
                await createAmendment({ variables: { input: { missionId, ...values } } });
                await graphqlConfig.refetchQueries({ include: [GET_MISSION_AMENDMENTS] });
            } finally {
                isCreatingRef.current = false;
                setIsLoading(false);
            }
        } else if (missionId && !amendmentHasChanges && missionDraftAmendment?.id) {
            setIsLoading(true);
            try {
                await deleteAmendment({ variables: { amendmentId: missionDraftAmendment.id } });
                await graphqlConfig.refetchQueries({ include: [GET_MISSION_AMENDMENTS] });
            } finally {
                setIsLoading(false);
            }
        } else if (!isCreatingRef.current) {
            setIsLoading(false);
        }
    }, [
        amendmentHasChanges,
        createAmendment,
        deleteAmendment,
        missionDraftAmendment,
        missionId,
        values,
    ]);

    const catchLazyQueryError = useCatchLazyQueryError();

    useEffect(() => {
        if (previousValues && !isEqual(values, previousValues)) {
            void (async () => {
                try {
                    const isUpdate = amendmentHasChanges && !!missionDraftAmendment;
                    if (isUpdate) {
                        setIsLoading(true);
                        await debouncedUpdateDraftAmendment();
                    } else {
                        debouncedUpdateDraftAmendment.cancel();
                        await createOrDeleteDraftAmendment();
                    }
                } catch (e) {
                    catchLazyQueryError(e as Error);
                }
            })();
        }
    }, [
        amendmentHasChanges,
        createOrDeleteDraftAmendment,
        debouncedUpdateDraftAmendment,
        missionDraftAmendment,
        values,
        previousValues,
        catchLazyQueryError,
    ]);

    return { isLoading };
};
